import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, PLATFORM_LABELS, STATUS_CONFIG, STATUS_FLOW } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, CartesianGrid, Tooltip,
  AreaChart, Area,
} from "recharts";
import { FolderOpen, AlertTriangle, Scale, CheckCircle2, Radar, ArrowUpRight, Gem } from "lucide-react";

function Kpi({ label, value, icon: Icon, testId, delay }) {
  return (
    <Card
      data-testid={testId}
      style={{ animationDelay: `${delay}ms` }}
      className="group animate-fade-up rounded-lg border-[#E8E4DF] bg-white p-6 shadow-[0_2px_16px_rgb(10,10,10,0.03)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_30px_rgb(10,10,10,0.06)]"
    >
      <div className="flex items-start justify-between">
        <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[#0A0A0A]">
          <Icon className="h-5 w-5 text-white" strokeWidth={1.6} />
        </div>
        <ArrowUpRight className="h-4 w-4 text-[#C9C7CB] transition-colors group-hover:text-[#0A0A0A]" />
      </div>
      <p className="mt-5 font-heading text-4xl font-bold tabular-nums text-[#0A0A0A]">{value}</p>
      <p className="mt-1 text-sm font-medium uppercase tracking-[0.12em] text-[#8B8A8E]">{label}</p>
    </Card>
  );
}

export default function Overview() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  const load = async () => {
    const [s, c] = await Promise.all([api.get("/stats"), api.get("/cases")]);
    setStats(s.data);
    setRecent(c.data.slice(0, 6));
  };

  useEffect(() => {
    load();
  }, []);

  const statusData = stats
    ? STATUS_FLOW.map((s) => ({ name: STATUS_CONFIG[s].label, key: s, value: stats.by_status[s] || 0 }))
    : [];
  const platformData = stats
    ? Object.entries(stats.by_platform).map(([k, v]) => ({ name: PLATFORM_LABELS[k] || k, value: v }))
    : [];

  return (
    <div className="space-y-8" data-testid="overview-page">
      <div className="relative overflow-hidden rounded-xl bg-[#0A0A0A] p-8 text-white shadow-sm md:p-10">
        <Gem className="absolute -right-4 -top-6 h-44 w-44 text-white/[0.04]" strokeWidth={0.8} />
        <div className="absolute -bottom-16 right-28 h-48 w-48 rounded-full bg-[#A6A4A9]/10 blur-3xl" />
        <div className="relative flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#A6A4A9]">STIG ROCK · Markenschutz</p>
            <h1 className="mt-3 font-heading text-3xl font-bold md:text-4xl">Übersicht</h1>
            <p className="mt-2 max-w-md text-sm text-white/70">
              Aktueller Stand aller erkannten Compliance-Verstöße und ihrer Bearbeitung.
            </p>
          </div>
          <Link to="/scanner">
            <Button
              data-testid="overview-scan-button"
              className="rounded-md bg-[#FAF7F2] px-5 py-6 text-base font-semibold tracking-wide text-[#0A0A0A] shadow-sm transition-all hover:-translate-y-0.5 hover:bg-white active:scale-[0.98]"
            >
              <Radar className="mr-2 h-5 w-5" /> Neue Suche starten
            </Button>
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi testId="kpi-total" delay={0} label="Fälle gesamt" value={stats?.total ?? "–"} icon={FolderOpen} />
        <Kpi testId="kpi-open" delay={60} label="Offen" value={stats?.open ?? "–"} icon={AlertTriangle} />
        <Kpi testId="kpi-legal" delay={120} label="Rechtsabteilung" value={stats?.legal ?? "–"} icon={Scale} />
        <Kpi testId="kpi-resolved" delay={180} label="Gelöst" value={stats?.resolved ?? "–"} icon={CheckCircle2} />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="rounded-lg border-[#E8E4DF] bg-white p-6 shadow-[0_2px_16px_rgb(10,10,10,0.03)]">
          <h3 className="font-heading text-lg font-bold text-[#0A0A0A]">Fälle nach Status</h3>
          <div className="mt-5 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={statusData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EDE9E3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#8B8A8E" }} interval={0} angle={-12} textAnchor="end" height={50} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#8B8A8E" }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "#F4F1EC" }} contentStyle={{ borderRadius: 6, border: "1px solid #E8E4DF", boxShadow: "0 8px 30px rgba(10,10,10,0.08)" }} />
                <Bar dataKey="value" radius={[3, 3, 0, 0]} maxBarSize={46}>
                  {statusData.map((d) => (
                    <Cell key={d.key} fill={STATUS_CONFIG[d.key].hex} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="rounded-lg border-[#E8E4DF] bg-white p-6 shadow-[0_2px_16px_rgb(10,10,10,0.03)]">
          <h3 className="font-heading text-lg font-bold text-[#0A0A0A]">Treffer nach Plattform</h3>
          <div className="mt-5 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={platformData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="platGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0A0A0A" stopOpacity={0.22} />
                    <stop offset="95%" stopColor="#0A0A0A" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#EDE9E3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#8B8A8E" }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#8B8A8E" }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ stroke: "#A6A4A9" }} contentStyle={{ borderRadius: 6, border: "1px solid #E8E4DF", boxShadow: "0 8px 30px rgba(10,10,10,0.08)" }} />
                <Area type="monotone" dataKey="value" stroke="#0A0A0A" strokeWidth={2.5} fill="url(#platGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Card className="overflow-hidden rounded-lg border-[#E8E4DF] bg-white shadow-[0_2px_16px_rgb(10,10,10,0.03)]">
        <div className="flex items-center justify-between border-b border-[#EDE9E3] px-6 py-5">
          <h3 className="font-heading text-lg font-bold text-[#0A0A0A]">Zuletzt gefunden</h3>
          <Link to="/fallakte" className="inline-flex items-center gap-1 text-sm font-semibold text-[#0A0A0A] hover:text-[#A6A4A9]">
            Alle Fälle <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
        {recent.length === 0 ? (
          <p className="px-6 py-12 text-center text-sm text-[#8B8A8E]">
            Noch keine Fälle. Starte eine neue Suche im Scanner.
          </p>
        ) : (
          <div className="divide-y divide-[#EDE9E3]">
            {recent.map((c) => (
              <Link
                key={c.id}
                to="/fallakte"
                data-testid={`recent-case-${c.id}`}
                className="flex items-center justify-between px-6 py-4 transition-colors hover:bg-[#FAF7F2]"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-[#E8E4DF] bg-[#F4F1EC] font-heading text-sm font-bold text-[#0A0A0A]">
                    {c.profile_name?.charAt(0) || "?"}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-[#0A0A0A]">{c.profile_name}</p>
                    <p className="truncate text-xs text-[#8B8A8E]">
                      {PLATFORM_LABELS[c.platform]} · {c.handle || c.matched_keyword} · KI {c.ai_confidence}%
                    </p>
                  </div>
                </div>
                <StatusBadge status={c.status} />
              </Link>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
